/**
 * SupplierFactoryBoundary (AJIP-16.02.009 · §21)
 * ---------------------------------------------------------------------
 * Correction 2 · retained solely to ENFORCE journey separation between
 * Supplier and Factory instances (§21).
 *
 * Directive requirements enforced:
 *   §21.1  Completion of Supplier Journey must NOT complete Factory
 *          Journey (and vice versa) — reference recording only.
 *   §21.2  Activation of one journey must NOT automatically activate
 *          the other.
 *   §21.3  Supplier trust / verification / reputation must NOT
 *          automatically transfer to a factory. This boundary carries
 *          NO trust or verification signal.
 *   §21.4  Shared organisational data may be REFERENCED where
 *          authorised, but journey state remains independently
 *          identifiable and auditable — the reference is opaque.
 *
 * The boundary is:
 *   • Validation-only.
 *   • Provider-independent.
 *   • Free of external-system imports.
 *   • Free of storage access.
 *   • Free of customer-facing behaviour.
 *   • Without authority over eligibility, activation, progression or
 *     completion.
 */
import { SupplierFactoryBoundaryError } from './FactoryJourneyErrors';

const MAX_REF_LEN = 128;
const REF_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:@\-/]{0,127}$/;

const isNonEmptyString = (v) => typeof v === 'string' && v.trim().length > 0;

const validateSupplierReferenceRef = (supplierReferenceRef) => {
  if (!isNonEmptyString(supplierReferenceRef)) {
    throw new SupplierFactoryBoundaryError('supplierReferenceRef must be non-empty string');
  }
  if (supplierReferenceRef.length > MAX_REF_LEN) {
    throw new SupplierFactoryBoundaryError('supplierReferenceRef exceeds max length');
  }
  if (!REF_PATTERN.test(supplierReferenceRef)) {
    throw new SupplierFactoryBoundaryError('supplierReferenceRef has invalid shape');
  }
  return true;
};

const attachSupplierReference = ({ factoryContext, supplierReferenceRef }) => {
  validateSupplierReferenceRef(supplierReferenceRef);
  return Object.freeze({ ...(factoryContext || {}), supplierReferenceRef });
};

// §21.3 — governance record ensuring no trust transfer occurs at this boundary.
const BOUNDARY_GOVERNANCE = Object.freeze({
  factory_may: 'record-opaque-supplier-reference',
  factory_shall_never: [
    'inherit-supplier-verification',
    'inherit-supplier-trust-score',
    'inherit-supplier-reputation',
    'infer-factory-verified-because-supplier-verified',
    'complete-factory-journey-via-supplier-completion',
    'activate-factory-journey-via-supplier-activation',
    'read-supplier-journey-state',
  ].join('|'),
});

const BOUNDARY_NAME = 'SupplierFactoryBoundary';

export {
  BOUNDARY_NAME,
  BOUNDARY_GOVERNANCE,
  validateSupplierReferenceRef,
  attachSupplierReference,
};
